import { ReactElement, useEffect, useRef, useState } from 'react';
import { ActionIcon, Avatar, Badge, Box, Card, Group, Stack, Text } from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import { alert } from '@/services/alert';
import AvailabilityProviderCardSkeleton from './AvailabilityProviderCardSkeleton';
import { AvailabilityEntry, AvailabilityModality, ProviderAvailability } from '../Availability/types';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { getReferenceString } from '@medplum/core';
import { AvailabilityType, AvailabilityTypeEnum } from '@bariendo/lib/scheduling';

const INITIAL_VISIBLE_COUNT = 3;
const BADGE_STYLE = { width: 'fit-content', fontSize: '12px', padding: '4px 10px', borderRadius: '12px' };

interface AvailabilityProviderCardProps {
  provider: ProviderAvailability;
  onEditAvailability: (providerId: string, availabilityId: string, type: AvailabilityType) => void;
  getOneTimeAvailability: (provider: ProviderAvailability, providerSchedule: string) => Promise<void>;
}

export function AvailabilityProviderCard({
  provider,
  onEditAvailability,
  getOneTimeAvailability,
}: AvailabilityProviderCardProps): ReactElement {
  const cardRef = useRef<HTMLDivElement>(null);
  const {
    currentUserPermissions: { canManageSchedules },
    currentUser,
  } = useCurrentUser();
  const hasLoadedRef = useRef(false);
  const [isLoading, setIsLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_COUNT);

  useEffect(() => {
    if (isLoading || hasLoadedRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(async (entry) => {
          if (!entry.isIntersecting || hasLoadedRef.current) return;

          hasLoadedRef.current = true;
          if (!provider.providerScheduleReference) {
            return;
          }

          setIsLoading(true);
          try {
            await getOneTimeAvailability(provider, provider.providerScheduleReference);
          } catch (error) {
            alert.error(
              "Couldn't load one-time availability for " +
                provider.providerName +
                ': ' +
                (error instanceof Error ? error.message : 'Unknown error')
            );
          } finally {
            setIsLoading(false);
          }
        });
      },
      { rootMargin: '100px' } // Start loading 100px before the card is visible
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => {
      if (cardRef.current) {
        observer.unobserve(cardRef.current);
      }
    };
  }, [getOneTimeAvailability, provider]);

  const hasMore = provider.availabilities.length > visibleCount;
  const visibleAvailabilities = expanded ? provider.availabilities : provider.availabilities.slice(0, visibleCount);

  const toggleExpansion = () => {
    if (expanded) {
      setVisibleCount(INITIAL_VISIBLE_COUNT);
      setExpanded(false);
    } else {
      setVisibleCount(provider.availabilities.length);
      setExpanded(true);
    }
  };

  const getModalityBadgeColor = (modality: AvailabilityModality): string => {
    return modality === 'Virtual' ? 'blue' : 'cyan';
  };

  const renderSchedule = (entry: AvailabilityEntry) => {
    if (entry.type === AvailabilityTypeEnum.WEEKLY) {
      const schedules = Array.isArray(entry.schedule) ? entry.schedule : [entry.schedule];
      return (
        <Stack gap={4}>
          {schedules.map((schedule, idx) => (
            <Text key={idx} size="sm">
              {schedule}
            </Text>
          ))}
        </Stack>
      );
    }

    return (
      <Stack gap={4}>
        <Text size="sm">{entry.schedule}</Text>
        {entry.services && entry.services.length > 0 && (
          <Text size="sm" c="dimmed">
            ({entry.services.join(', ')})
          </Text>
        )}
      </Stack>
    );
  };

  const handleEditAvailability = (providerId: string, availabilityId: string, type: AvailabilityType) => {
    onEditAvailability(providerId, availabilityId, type);
  };

  return (
    <Card ref={cardRef} withBorder padding={0} radius="lg" style={{ backgroundColor: '#ffffff' }} w="100%">
      {isLoading ? (
        <AvailabilityProviderCardSkeleton />
      ) : (
        <Stack gap={0}>
          {/* Provider Header */}
          <Box
            p="xs"
            style={{
              borderBottom: '1px solid #e9ecef',
            }}
          >
            <Group gap="md">
              <Avatar size="md" radius="xl" color="gray">
                {provider.providerInitials}
              </Avatar>
              <Text fw={600} size="md">
                {provider.providerName}
              </Text>
            </Group>
          </Box>

          {/* Availability Entries */}
          <Box p="lg">
            <Stack gap={0}>
              {visibleAvailabilities.map((availability, idx) => (
                <Box
                  key={availability.id}
                  py={12}
                  style={{ borderBottom: idx < visibleAvailabilities.length - 1 ? '1px solid #e0e0e0' : 'none' }}
                >
                  <Group gap="md" align="flex-start" wrap="nowrap">
                    {/* Badges Column */}
                    <Stack gap={4} style={{ minWidth: '140px', alignItems: 'flex-start', paddingRight: '8px' }}>
                      {availability.modality === 'In Person' && (
                        <Badge size="sm" color="gray" variant="light" style={BADGE_STYLE}>
                          {availability.location || 'N/A'}
                        </Badge>
                      )}
                      {availability.modality === 'Virtual' && (
                        <Badge
                          size="sm"
                          color={getModalityBadgeColor(availability.modality)}
                          variant="light"
                          style={BADGE_STYLE}
                        >
                          {availability.modality}
                        </Badge>
                      )}
                    </Stack>

                    {/* Schedule Details Column */}
                    <Stack gap={4} style={{ flex: 1 }}>
                      <Text fw={600} size="sm">
                        {availability.type === AvailabilityTypeEnum.WEEKLY ? 'Weekly:' : 'One-time:'}
                      </Text>
                      {renderSchedule(availability)}
                    </Stack>

                    {/* Edit Icon Column */}
                    {(canManageSchedules ||
                      (currentUser && provider.providerId === getReferenceString(currentUser))) && (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={() => handleEditAvailability(provider.providerId, availability.id, availability.type)}
                        style={{
                          flexShrink: 0,
                          opacity: 0.6,
                          transition: 'opacity 0.2s ease-in-out',
                          marginLeft: 'auto',
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = '1')}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = '0.6')}
                      >
                        <IconPencil size={16} />
                      </ActionIcon>
                    )}
                  </Group>
                </Box>
              ))}
            </Stack>

            {/* Show More/Less Toggle */}
            {hasMore && (
              <Box pt={8} pl={56}>
                <Text size="sm" c="blue" style={{ cursor: 'pointer' }} onClick={toggleExpansion}>
                  {expanded ? 'Show Less...' : 'Show More...'}
                </Text>
              </Box>
            )}
          </Box>
        </Stack>
      )}
    </Card>
  );
}
